import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import axiosInstance from "api/axiosInstance";
import { API_ENDPOINTS } from "api/endpoints";
import { AdminUser, AuthState, LoginRequest, LoginResponse } from "types/auth";

const ADMIN_TOKEN_KEY = "admin_token";
const ADMIN_REFRESH_TOKEN_KEY = "admin_refresh_token";
const ADMIN_USER_KEY = "admin_user";

const allowedAdminRoles = ["super_admin", "admin"];

const clearLegacyAdminLocalStorage = () => {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY);
  localStorage.removeItem(ADMIN_USER_KEY);
};

const getStoredAdmin = (): AdminUser | null => {
  const admin = sessionStorage.getItem(ADMIN_USER_KEY);

  if (!admin) {
    return null;
  }

  try {
    return JSON.parse(admin) as AdminUser;
  } catch {
    sessionStorage.removeItem(ADMIN_USER_KEY);
    return null;
  }
};

export const isAllowedAdminRole = (role?: string | null) =>
  Boolean(role && allowedAdminRoles.includes(role));

export const clearAdminAuthStorage = () => {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  sessionStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(ADMIN_USER_KEY);
  clearLegacyAdminLocalStorage();
};

const getInitialState = (): AuthState => {
  clearLegacyAdminLocalStorage();

  const token = sessionStorage.getItem(ADMIN_TOKEN_KEY);
  const refreshToken = sessionStorage.getItem(ADMIN_REFRESH_TOKEN_KEY);
  const admin = getStoredAdmin();
  const isAuthenticated = Boolean(
    token && admin && isAllowedAdminRole(admin.role)
  );

  if (token && admin && !isAllowedAdminRole(admin.role)) {
    clearAdminAuthStorage();
  }

  return {
    admin: isAuthenticated ? admin : null,
    token: isAuthenticated ? token : null,
    refreshToken: isAuthenticated ? refreshToken : null,
    isAuthenticated,
    loading: false,
    error: null,
  };
};

const getLoginErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string; success?: boolean }>;
    const status = axiosError.response?.status;

    if (status === 401 || status === 400) {
      return "Invalid credentials";
    }

    if (status && status >= 500) {
      return "Something went wrong. Please try again.";
    }

    if (!axiosError.response) {
      return "Network error. Please try again.";
    }

    return axiosError.response.data?.message || "Something went wrong. Please try again.";
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
};

export const loginAdmin = createAsyncThunk<
  { admin: AdminUser; token: string; refreshToken: string },
  LoginRequest,
  { rejectValue: string }
>("auth/loginAdmin", async (credentials, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<LoginResponse>(
      API_ENDPOINTS.auth.login,
      credentials
    );

    const loginData = response.data;

    if (!loginData.success) {
      return rejectWithValue(loginData.message || "Invalid credentials");
    }

    if (!loginData.authentication) {
      return rejectWithValue("Something went wrong. Please try again.");
    }

    if (!loginData.refresh) {
      return rejectWithValue("Something went wrong. Please try again.");
    }

    if (!loginData.data) {
      return rejectWithValue("Something went wrong. Please try again.");
    }

    if (!isAllowedAdminRole(loginData.data.role)) {
      clearAdminAuthStorage();
      return rejectWithValue("You are not authorized to access admin panel.");
    }

    clearLegacyAdminLocalStorage();
    sessionStorage.setItem(ADMIN_TOKEN_KEY, loginData.authentication);
    sessionStorage.setItem(ADMIN_REFRESH_TOKEN_KEY, loginData.refresh);
    sessionStorage.setItem(ADMIN_USER_KEY, JSON.stringify(loginData.data));

    return {
      admin: loginData.data,
      token: loginData.authentication,
      refreshToken: loginData.refresh,
    };
  } catch (error) {
    return rejectWithValue(getLoginErrorMessage(error));
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState: getInitialState(),
  reducers: {
    logoutAdmin: (state) => {
      clearAdminAuthStorage();
      state.admin = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
    },
    clearAuthError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        loginAdmin.fulfilled,
        (
          state,
          action: PayloadAction<{
            admin: AdminUser;
            token: string;
            refreshToken: string;
          }>
        ) => {
          state.admin = action.payload.admin;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.isAuthenticated = true;
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(loginAdmin.rejected, (state, action) => {
        clearAdminAuthStorage();
        state.admin = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = action.payload || "Something went wrong. Please try again.";
      });
  },
});

export const { clearAuthError, logoutAdmin } = authSlice.actions;
export default authSlice.reducer;
